import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// A high-voltage lattice transmission tower (pylon): four steel legs splay from
// a wide base to a pinched waist, the body rises with cross-bracing, two
// crossarms carry hanging insulator strings, and an earth peak tops it off. The
// lattice is faked with thin struts placed between named points by `strut`.
const STEEL = "#878e96";
const STEEL_DK = "#6c727a";
const INSUL = "#8f9aa0";

// A thin square member spanning a->b, oriented along its length.
function strut(a: Vec3, b: Vec3, thick: number, color: string): Part {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const len = Math.hypot(dx, dy, dz) || 1e-4;
  const mid: Vec3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
  const pitch = Math.acos(Math.max(-1, Math.min(1, dy / len)));
  const yaw = Math.atan2(dx, dz);
  return { geometry: new THREE.BoxGeometry(thick, len, thick), color, position: mid, rotation: [pitch, yaw, 0], eulerOrder: "YXZ" };
}

// A horizontal square belt (ring of four members) at height y, half-width h.
function belt(y: number, h: number, thick: number): Part[] {
  const c: Vec3[] = [[-h, y, -h], [h, y, -h], [h, y, h], [-h, y, h]];
  return c.map((p, i) => strut(p, c[(i + 1) % 4], thick, STEEL_DK));
}

// An insulator string of discs hanging from (x, y, z).
function insulatorString(x: number, y: number, z: number): Part[] {
  const out: Part[] = [];
  for (let i = 0; i < 3; i++) {
    out.push({ geometry: new THREE.CylinderGeometry(0.035, 0.035, 0.04, 8), color: INSUL, position: [x, y - 0.05 - i * 0.06, z] });
  }
  out.push({ geometry: new THREE.BoxGeometry(0.05, 0.05, 0.05), color: STEEL_DK, position: [x, y - 0.26, z] });
  return out;
}

export const transmissionTower = defineModel({
  id: "transmission-tower",
  batchId: "electric",
  name: "Transmission tower",
  tags: ["building", "electric", "power", "grid", "pylon", "transmission", "high-voltage"],
  summary: "A steel lattice pylon carrying high-voltage lines on insulator strings.",
  footprint: 0.7,
  parts(): Part[] {
    const baseY = 0.14, waistY = 1.0, topY = 1.7;
    const bc = 0.45, wc = 0.17, tc = 0.19; // base / waist / top corner half-widths
    const corners = (h: number, y: number): Vec3[] => [[-h, y, -h], [h, y, -h], [h, y, h], [-h, y, h]];
    const base = corners(bc, baseY), waist = corners(wc, waistY), top = corners(tc, topY);
    const parts: Part[] = [
      { geometry: new THREE.BoxGeometry(1.1, 0.14, 1.1), color: "#5a5a5e", position: [0, 0.07, 0] },
    ];
    // Four splayed legs and the vertical body above the waist.
    for (let i = 0; i < 4; i++) {
      parts.push(strut(base[i], waist[i], 0.06, STEEL));
      parts.push(strut(waist[i], top[i], 0.05, STEEL));
    }
    // Belts and X-bracing on the front and back faces (lattice feel).
    parts.push(...belt(0.55, (bc + wc) / 2 + 0.02, 0.035), ...belt(waistY, wc, 0.035), ...belt(1.35, (wc + tc) / 2, 0.03));
    for (const z of [-1, 1]) {
      parts.push(strut([-bc, baseY, z * bc], [wc, waistY, z * wc], 0.03, STEEL_DK));
      parts.push(strut([bc, baseY, z * bc], [-wc, waistY, z * wc], 0.03, STEEL_DK));
    }
    // Two crossarms with insulator strings, plus an earth peak.
    for (const [y, reach] of [[1.45, 0.62], [1.7, 0.46]] as const) {
      parts.push({ geometry: new THREE.BoxGeometry(reach * 2, 0.06, 0.09), color: STEEL, position: [0, y, 0] });
      parts.push(...insulatorString(-reach, y - 0.03, 0), ...insulatorString(reach, y - 0.03, 0), ...insulatorString(0, y - 0.03, 0));
    }
    parts.push(strut([-tc, topY, 0], [0, topY + 0.22, 0], 0.04, STEEL));
    parts.push(strut([tc, topY, 0], [0, topY + 0.22, 0], 0.04, STEEL));
    parts.push({ geometry: new THREE.ConeGeometry(0.035, 0.12, 6), color: STEEL_DK, position: [0, topY + 0.26, 0] });
    return parts;
  },
  animations: [],
});
